// @flow

import React, { type Node } from 'react';
import styled from 'styled-components';
import { InputMask, formatValueWithMask } from '@tectonics/components/input-mask';
import { useUniqueId } from '@tectonics/hooks';
import { type CardBrand } from '@tectonics/prs/card-brand';
import { useUserInputAsTextFieldWithFormatting } from '@tectonics/prs/user-input-values';
import { useSubmitting } from '@tectonics/prs/submitting';
import { useSelectedCardBrand } from '@tectonics/look-and-feel/commons/selected-card-brand';
import { Label, TextField } from '../../../../../../commons/atoms';
import { InputGroup } from '../../../../../../commons/molecules';
import { useUserInputValidationError, type UserInputValidationError, serverErrorMessage } from '@tectonics/prs/validation';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { ErrorLabel, InputHolder } from '../../commons';
import cardGenericBackSvgSrc from './images/credit-card-generic-back.svg';
import { translate } from '@tectonics/translate';

type SecurityCodeMask = ImmutableArray<RegExp | string>;

export function InputCardSecurityCode(): Node {
  const inputId = useUniqueId();
  const brand = useSelectedCardBrand();
  const mask = getSecurityCodeMask(brand);
  const formHasBeenSent = useFormHasBeenSent();
  const validationError = useUserInputValidationError('cardSecurityCode');
  const submitting = useSubmitting();

  const [value, handleChange] = useUserInputAsTextFieldWithFormatting('cardSecurityCode', formatRaw(mask), parseFormatted);

  return (
    <InputHolder data-test-name="card-security-code" style={{ gridColumn: '2/3' }}>
      <InputGroup
        label={
          <Label htmlFor={inputId}>
            {getCountryBasedMessage()}
          </Label>}
        input={
          <InputFieldWrapper>
            <TextField
              id={inputId}
              as={InputMask}
              placeholder="000"
              mask={mask}
              type="tel"
              value={value}
              onChange={handleChange}
              fashion={formHasBeenSent && validationError ? 'error' : null}
              readOnly={submitting}
              data-error={formHasBeenSent && validationError ? 'error' : null}
              data-secret
              name="card-security-code"
            />
            <IconWrapper>
              <CardImage src={cardGenericBackSvgSrc} alt="card cvv icon" />
            </IconWrapper>
          </InputFieldWrapper>
        }
      />
      {formHasBeenSent && validationError && (
        <ValidationErrorMessage validationError={validationError} />
      )}
    </InputHolder>
  );
}

function getCountryBasedMessage(): string {
  return translate('shopify.inputCardSecurityCode');
}

const InputFieldWrapper = styled.div`
  position: relative;
  & > * {
    width: 100%;
  }
  & > span {
    width: auto;
  }
`;

const IconWrapper = styled.span`
  position: absolute;
  pointer-events: none;
  right: 15px;
  top: 50%;
  margin-top: -10px;
`;

const CardImage = styled.img``;

type ValidationErrorMessageProps = {|
  validationError: UserInputValidationError,
|};

const messages = (validationError: UserInputValidationError) => {
  switch(validationError.id){
    case 'empty':
      return translate('shopify.fillCardSecurityCode');

    case 'invalid-pattern':
      return translate('shopify.invalidSecurityCode');

    default:
      return translate('shopify.invalidCvv');
  }
};

function ValidationErrorMessage({ validationError }: ValidationErrorMessageProps): Node {
  return (
    <ErrorLabel>
      {messages(validationError) || (serverErrorMessage(validationError.payload))}
    </ErrorLabel>
  );
}

function formatRaw(mask: SecurityCodeMask): (rawValue: string) => string {
  return (rawValue: string): string => formatValueWithMask(rawValue, mask);
}

function parseFormatted(formattedValue: string): string {
  return formattedValue.replace(/[^0-9]+/ug, '');
}

function getSecurityCodeMask(cardBrand: ?CardBrand): SecurityCodeMask {
  switch (cardBrand) {
    case 'amex':
      return [/[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/];

    default:
      return [/[0-9]/, /[0-9]/, /[0-9]/];
  }
}
